import type { IssuedCredentials } from '../credentials/issued-credentials'
import { useMutation } from '@apollo/client/react'
import { useRef } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import FieldError from '~/components/FieldError'
import { Input } from '~/components/ui/input'
import { Label } from '~/components/ui/label'
import { PasswordInput } from '~/components/ui/password-input'
import { GQL_CREATE_USERNAME_LEARNER } from '~/gql/users/username-learners'
import { MIN_PASSWORD_LENGTH } from '~/helpers/auth/password-rule'
import { errorAria } from '~/helpers/forms/error-aria'
import { userFullName } from '~/helpers/users/display-name'
import { isValidUsername, normalizeUsername, usernameErrorKey } from '~/helpers/users/username'
import AffiliationFields from './AffiliationFields'
import GroupNameSelect from './GroupNameSelect'

export const USERNAME_LEARNER_FORM_ID = 'username-learner-form'

/** Au-delà, le mot de passe ne tient plus sur la fiche : même borne que l'API. */
const MAX_ISSUED_PASSWORD_LENGTH = 128

type FormData = { username: string, lastName: string, firstName: string, password: string, group: string, company: string, department: string }

type IProps = {
  /** Le compte est créé : ses identifiants, à remettre tout de suite. */
  onCreated: (credentials: IssuedCredentials) => void
}

/**
 * Création d'un apprenant sans e-mail (#271) : pseudo, nom facultatif, mot de
 * passe saisi ou généré par l'API. Toujours un apprenant, aucun mail envoyé.
 */
function UsernameLearnerForm({ onCreated }: IProps) {
  const { t } = useTranslation()
  const { register, handleSubmit, setValue, watch, setError, formState } = useForm<FormData>({
    defaultValues: { username: '', lastName: '', firstName: '', password: '', group: '', company: '', department: '' },
  })
  const [create] = useMutation(GQL_CREATE_USERNAME_LEARNER)
  const submitting = useRef(false)

  const onSubmit = async (values: FormData) => {
    // Le bouton « Créer » est dans le pied du panneau, hors de ce formulaire : un second clic ne doit pas créer deux fois.
    if (submitting.current) return
    submitting.current = true
    try {
      const { data } = await create({
        variables: {
          input: {
            username: normalizeUsername(values.username),
            firstName: values.firstName.trim() || undefined,
            lastName: values.lastName.trim() || undefined,
            password: values.password || undefined,
            group: values.group || undefined,
            company: values.company || undefined,
            department: values.department || undefined,
          },
        },
      })
      if (!data) return
      const name = userFullName(values)
      toast.success(t('good.add.usernameLearner'))
      onCreated({ ...data.createUsernameLearner, ...(name && { name }) })
    } catch (error) {
      const key = usernameErrorKey(error instanceof Error ? error.message : '')
      if (key?.startsWith('user.password.')) setError('password', { message: t(key) })
      else if (key) setError('username', { message: t(key) })
      else toast.error(t('bad.add.user'))
    } finally {
      submitting.current = false
    }
  }

  return (
    <form id={USERNAME_LEARNER_FORM_ID} onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="username" required>{t('common.username')}</Label>
        <Input
          id="username"
          aria-required
          autoCapitalize="none"
          spellCheck={false}
          {...errorAria('username', formState.errors.username)}
          aria-describedby={formState.errors.username ? 'username-help username-error' : 'username-help'}
          {...register('username', {
            required: t('input.required.message'),
            validate: value => isValidUsername(value) || t('user.username.invalid'),
          })}
        />
        <p id="username-help" className="text-xs text-muted-foreground">{t('user.username.help')}</p>
        <FieldError fieldId="username" message={formState.errors.username?.message} />
      </div>

      <p className="text-xs text-muted-foreground">{t('user.nameOptional.help')}</p>
      <div className="space-y-2">
        <Label htmlFor="username-lastName">{t('common.lastname')}</Label>
        <Input id="username-lastName" {...register('lastName')} />
      </div>
      <div className="space-y-2">
        <Label htmlFor="username-firstName">{t('common.firstname')}</Label>
        <Input id="username-firstName" {...register('firstName')} />
      </div>
      <AffiliationFields idPrefix="username" company={register('company')} department={register('department')} />

      <div className="space-y-2">
        <Label htmlFor="username-password">{t('common.password')}</Label>
        <PasswordInput
          id="username-password"
          autoComplete="new-password"
          {...errorAria('username-password', formState.errors.password)}
          aria-describedby={formState.errors.password ? 'username-password-help username-password-error' : 'username-password-help'}
          {...register('password', {
            validate: value => !value || (value.length < MIN_PASSWORD_LENGTH ? t('user.password.tooShort') : value.length > MAX_ISSUED_PASSWORD_LENGTH ? t('user.password.tooLong') : true),
          })}
        />
        <p id="username-password-help" className="text-xs text-muted-foreground">{t('user.password.optional.help')}</p>
        <FieldError fieldId="username-password" message={formState.errors.password?.message} />
      </div>

      <GroupNameSelect id="username-group" value={watch('group')} onChange={value => setValue('group', value)} />
    </form>
  )
}

export default UsernameLearnerForm
